/**
 * Consequence-specific confirmation primitive. The body MUST describe what
 * the action will do (not "are you sure?"). For hard deletes of named
 * entities, pass `requireTypedConfirmation` so the operator types the name
 * to enable the action.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  severity?: 'primary' | 'warning' | 'error';
  requireTypedConfirmation?: string | undefined;
  busy?: boolean | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  severity = 'primary',
  requireTypedConfirmation,
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useCopy();
  const [typed, setTyped] = useState('');
  const typedOk = !requireTypedConfirmation || typed.trim() === requireTypedConfirmation;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{body}</DialogContentText>
        {requireTypedConfirmation ? (
          <TextField
            fullWidth
            size="small"
            label={`Type "${requireTypedConfirmation}" to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            sx={{ mt: 2 }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={Boolean(busy)}>
          {cancelLabel ?? t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          color={severity === 'primary' ? 'primary' : severity}
          onClick={onConfirm}
          disabled={Boolean(busy) || !typedOk}
        >
          {confirmLabel ?? t('common.delete', 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
